import React from 'react';

import { useThemeMode } from '@/nova/theme/OwsTheme';
import type { ResolvedMode } from '@/nova/theme/themeConfig';

import { Text } from '../../../../../components';
import type { DeploymentStepState } from '../../../../../mock-data/types';
import { resolveStepMeta, stepArtworkChain } from '../stepMeta';
import type { CardStatus } from '../types';
import {
  Card,
  Artwork,
  ScrimTop,
  ScrimBottom,
  Header,
  CardTitle,
  TitleIndex,
  Footer,
  StatusDot,
  StatusLabel,
} from './BounceCard.styles';

export type BounceCardProps = {
  /** Resolved card state (never `confirm` — that renders ConfirmationCard). */
  status: Exclude<CardStatus, 'confirm'>;
  label: string;
  /** 1-based position in the deploy timeline — shown as the "{n}." title prefix. */
  stepNumber: number;
  /** When reduced motion is on, the in-progress pulse never animates. */
  reduced: boolean;
  /** Forces which artwork set to use instead of following the resolved studio theme. Only
   *  the CT spec passes it (the harness cannot mount the theme provider); app callers
   *  leave it off so the cards track Settings. Mirrors AuroraField's prop of the same name. */
  mode?: ResolvedMode;
};

/** Map the card's visual state onto the shared per-step colour vocabulary. */
const toStepState = (status: BounceCardProps['status']): DeploymentStepState =>
  status === 'in-progress' ? 'active' : status;

/** Non-active footer labels (already-extracted keys reused elsewhere). */
const STATE_LABEL: Record<'completed' | 'failed' | 'skipped' | 'pending', string> = {
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  pending: 'Pending',
};

/**
 * Walk to the next artwork candidate when one fails to load (its file isn't uploaded yet, or
 * 404s). The chain comes from {@link stepArtworkChain} and is theme-aware, so on light a
 * step whose `-light` file is still missing lands on the light placeholder instead of a dark
 * plate. The stage index advances at most to the end of the chain, so a missing last resort
 * cannot loop.
 *
 * Written imperatively on the element (the standard `onError` idiom, no re-render), which is
 * why the <img> carries a `key` tied to the chain head: a theme switch mounts a fresh element
 * rather than leaving this stage index stale.
 */
const advanceArtwork = (e: React.SyntheticEvent<HTMLImageElement>, chain: readonly string[]): void => {
  const img = e.currentTarget;
  const next = Number(img.dataset.artworkStage ?? '0') + 1;
  if (next >= chain.length) return;
  img.dataset.artworkStage = String(next);
  img.src = chain[next];
};

/**
 * One deploy-step card (Figma node 9103:92993). No border: artwork background + scrims, the
 * numbered step title ("{n}. {name}") up top, and a status footer at the bottom. In-progress
 * shows a glowing blue dot + "In progress" (animated); completed/failed/skipped/pending swap
 * the dot colour + label.
 */
export const BounceCard = React.forwardRef<HTMLElement, BounceCardProps>(
  ({ status, label, stepNumber, reduced, mode }, ref) => {
    const stepState = toStepState(status);
    const meta = resolveStepMeta(label);
    // The artwork set follows the studio theme; the title and status colours already do,
    // through their tokens. Hook called unconditionally, prop takes precedence.
    const resolvedTheme = useThemeMode();
    const artworks = stepArtworkChain(label, mode ?? resolvedTheme);

    return (
      <Card ref={ref} data-status={status} data-glow={status === 'in-progress' && !reduced ? 'on' : 'off'}>
        <Artwork
          key={artworks[0]}
          src={artworks[0]}
          alt=""
          aria-hidden="true"
          onError={(e) => advanceArtwork(e, artworks)}
        />
        <ScrimTop aria-hidden="true" />
        <ScrimBottom aria-hidden="true" />

        <Header>
          <CardTitle>
            <TitleIndex>{stepNumber}.</TitleIndex> <Text text={meta.title} tag="span" />
          </CardTitle>
        </Header>

        <Footer>
          <StatusDot $state={stepState} />
          <StatusLabel $state={stepState}>
            {status === 'in-progress' ? (
              <Text text="In progress" tag="span" />
            ) : (
              <Text text={STATE_LABEL[status]} tag="span" />
            )}
          </StatusLabel>
        </Footer>
      </Card>
    );
  },
);

BounceCard.displayName = 'BounceCard';

export default BounceCard;
